import AnswerFileUpload from '../AnswerFileUpload';
import FileAttachments from '../FileAttachments';
import QuestionOptionFile from '../QuestionOptionFile';
import RichTextContent from '../RichTextContent';
import { QuestionFeedbackBlock } from '../SubmissionFeedback';
import { getQuestionTypeLabel, isQuestionAnswered } from '../../utils/questionUtils';
import { focusTheme } from '../../utils/focusTheme';

function ArrowCircleIcon({ className = 'text-teal-500' }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <path d="M8.63612 5.41078L6.69336 7.21477C6.38802 7.4983 6.38802 7.98157 6.69336 8.2651L8.63612 10.0691M7.70447 0.537475C3.74645 0.537475 0.537843 3.74608 0.537843 7.7041C0.537843 11.6621 3.74645 14.8707 7.70447 14.8707C11.6625 14.8707 14.8711 11.6621 14.8711 7.7041C14.8711 3.74608 11.6625 0.537475 7.70447 0.537475Z" stroke="currentColor" strokeWidth="1.07499" strokeLinecap="round" />
    </svg>
  );
}

function getOptionPresentation({ canShowCorrectAnswers, isCorrect, isSelected }) {
  if (canShowCorrectAnswers && isCorrect) {
    return {
      buttonClass: focusTheme.option.correct,
      badgeClass: focusTheme.option.correctBadge,
      label: 'پاسخ صحیح',
    };
  }

  if (canShowCorrectAnswers && isSelected) {
    return {
      buttonClass: focusTheme.option.incorrect,
      badgeClass: focusTheme.option.incorrectBadge,
      label: 'پاسخ شما',
    };
  }

  if (isSelected) {
    return {
      buttonClass: focusTheme.option.selected,
      badgeClass: focusTheme.option.selectedBadge,
      label: '',
    };
  }

  return {
    buttonClass: focusTheme.option.unselected,
    badgeClass: focusTheme.option.unselectedBadge,
    label: '',
  };
}

function ActivityQuestionCard({
  answer = {},
  canAnswer = true,
  canShowCorrectAnswers = false,
  entityId,
  index,
  onChange,
  question,
  showAnswerScore = true,
  showQuestionFeedback = true,
  slug,
}) {
  const isAnswered = isQuestionAnswered(question, answer);

  return (
    <div
      className={`flex flex-col gap-3 p-3 rounded-[8px] border text-slate-800 ${isAnswered ? 'border-emerald-200' : 'border-teal-200 shadow-sm'}`}
      style={{ background: isAnswered ? focusTheme.question.answered : focusTheme.question.unanswered }}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <p className="text-[14px] font-semibold text-slate-900">سوال {index + 1}</p>
          <span className={`rounded-[4px] px-2 py-0.5 text-[11px] ${isAnswered ? 'bg-emerald-100 text-emerald-700' : 'bg-teal-100 text-teal-700'}`}>
            {question.score || 0} نمره
          </span>
        </div>

        <div className="flex items-center gap-2">
          <p className={`text-[12px] font-medium ${isAnswered ? 'text-emerald-700' : 'text-teal-700'}`}>
            {isAnswered ? 'پاسخ داده شده' : getQuestionTypeLabel(question.type)}
          </p>
          <ArrowCircleIcon className={isAnswered ? 'text-emerald-500' : 'text-teal-500'} />
        </div>
      </div>

      <RichTextContent
        className="text-[14px] font-semibold leading-7 text-slate-900"
        fallback="متن سوال ثبت نشده است."
        html={question.text}
      />

      <FileAttachments
        attachments={question.attachments}
        title="فایل‌های پیوست سوال"
        variant="embedded"
      />

      {question.type === 'instructional' ? (
        <p className="rounded-[8px] border border-teal-200 bg-white px-3 py-2 text-[13px] leading-6 text-slate-700">
          برای تکمیل این دستورالعمل، فایل پاسخ را بارگذاری کن.
        </p>
      ) : question.type === 'multiple_choice' ? (
        <div className="grid grid-cols-1 gap-2 pb-1">
          {(question.options || []).map((option, optionIndex) => {
            const hasCorrectOption = Boolean(question.correctOptionId);
            const isSelected = answer.selectedOptionId === option.id;
            const isCorrect = Boolean(canShowCorrectAnswers && hasCorrectOption && question.correctOptionId === option.id);
            const presentation = getOptionPresentation({
              canShowCorrectAnswers: canShowCorrectAnswers && hasCorrectOption,
              isCorrect,
              isSelected,
            });

            return (
              <button
                key={option.id}
                type="button"
                onClick={() => onChange(question.id, { selectedOptionId: option.id, textAnswer: '' })}
                disabled={!canAnswer}
                className={`flex flex-col items-stretch gap-2 rounded-[5px] border px-3 py-2 text-right text-[13px] font-semibold disabled:cursor-default ${presentation.buttonClass}`}
              >
                <div className="flex items-center gap-2">
                  <span className={`flex h-[24px] w-[24px] shrink-0 items-center justify-center rounded-[4px] text-[11px] ${presentation.badgeClass}`}>
                    {optionIndex + 1}
                  </span>
                  <RichTextContent
                    as="span"
                    className="flex-1"
                    fallback="گزینه بدون متن"
                    html={option.text}
                  />
                  {presentation.label && <span className="text-[11px] text-slate-600">{presentation.label}</span>}
                </div>
                <QuestionOptionFile url={option.url} />
              </button>
            );
          })}
        </div>
      ) : (
        <textarea
          value={answer.textAnswer || ''}
          onChange={(event) => onChange(question.id, { textAnswer: event.target.value, selectedOptionId: '' })}
          rows={4}
          disabled={!canAnswer}
          className="w-full resize-none rounded-[8px] border border-teal-200 bg-white px-3 py-2 text-[14px] leading-7 text-slate-800 outline-none placeholder:text-slate-400 disabled:bg-emerald-50"
          placeholder="پاسخت رو اینجا بنویس..."
        />
      )}

      <AnswerFileUpload
        disabled={!canAnswer}
        entityId={entityId}
        file={answer.file}
        onFileChange={(file) => onChange(question.id, { file })}
        questionId={question.id}
        slug={slug}
      />

      {showQuestionFeedback && (
        <QuestionFeedbackBlock
          answer={question.answer}
          accentColor={focusTheme.accent}
          showScore={showAnswerScore}
        />
      )}
    </div>
  );
}

export default ActivityQuestionCard;
